import React, { useCallback, useMemo, useState, RefObject, useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { useQueryClient } from '@tanstack/react-query';
import { FlashList } from '@shopify/flash-list';
import { useFocusEffect } from 'expo-router/react-navigation';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';

/**
 * Event system for triggering page refreshes from navigation components
 */
class PageRefreshEventManager {
  private static instance: PageRefreshEventManager;
  private listeners: Map<string, () => void> = new Map();

  static getInstance(): PageRefreshEventManager {
    if (!PageRefreshEventManager.instance) {
      PageRefreshEventManager.instance = new PageRefreshEventManager();
    }
    return PageRefreshEventManager.instance;
  }

  registerRefreshHandler(pageKey: string, handler: () => void) {
    this.listeners.set(pageKey, handler);
  }

  unregisterRefreshHandler(pageKey: string) {
    this.listeners.delete(pageKey);
  }

  triggerRefresh(pageKey: string) {
    const handler = this.listeners.get(pageKey);
    if (handler) {
      handler();
    }
  }
}

/**
 * Trigger refresh for a specific page from navigation components
 * @param pageKey - Unique identifier for the page, the route path with leading slash (e.g., '/feeds/global', '/people/online')
 */
export function triggerPageRefresh(pageKey: string) {
  const eventManager = PageRefreshEventManager.getInstance();
  eventManager.triggerRefresh(pageKey);
}

/**
 * Event system for resetting mounted page state during sideways navigation.
 * This is used by large-screen navigation when switching between tab families:
 * the destination list may still be mounted, so simply clearing persisted state
 * is not enough to move it back to the top.
 */
class PageNavigationResetEventManager {
  private static instance: PageNavigationResetEventManager;
  private listeners: Map<string, () => void> = new Map();

  static getInstance(): PageNavigationResetEventManager {
    if (!PageNavigationResetEventManager.instance) {
      PageNavigationResetEventManager.instance = new PageNavigationResetEventManager();
    }
    return PageNavigationResetEventManager.instance;
  }

  registerResetHandler(pageKey: string, handler: () => void) {
    this.listeners.set(pageKey, handler);
  }

  unregisterResetHandler(pageKey: string) {
    this.listeners.delete(pageKey);
  }

  triggerReset(pageKey: string) {
    const handler = this.listeners.get(pageKey);
    if (handler) {
      handler();
    }
  }
}

/**
 * Reset scroll/persistence state for a mounted page during navigation.
 * @param pageKey - Unique identifier for the page, the route path with leading slash (e.g., '/feeds/global', '/people/online')
 */
export function triggerPageNavigationReset(pageKey: string) {
  const eventManager = PageNavigationResetEventManager.getInstance();
  eventManager.triggerReset(pageKey);
}

/**
 * Global version counter that increments on filter changes (e.g. My Type Filter toggle).
 * Screens compare against their last-seen version on focus to detect missed changes.
 */
let filterVersion = 0;

export function getFilterVersion() {
  return filterVersion;
}

export function incrementFilterVersion() {
  filterVersion++;
}

/**
 * Event system for notifying all registered lists when a global filter changes.
 * Focused screens handle the event immediately; non-focused screens defer to useFocusEffect.
 */
class FilterChangedEventManager {
  private static instance: FilterChangedEventManager;
  private handlers: Map<string, () => void> = new Map();

  static getInstance(): FilterChangedEventManager {
    if (!FilterChangedEventManager.instance) {
      FilterChangedEventManager.instance = new FilterChangedEventManager();
    }
    return FilterChangedEventManager.instance;
  }

  register(key: string, handler: () => void) {
    this.handlers.set(key, handler);
  }

  unregister(key: string) {
    this.handlers.delete(key);
  }

  triggerAll() {
    this.handlers.forEach((handler) => handler());
  }
}

/**
 * Notify all registered lists that a global filter changed.
 * Only focused screens will act immediately; others defer to their focus effect.
 */
export function triggerFilterChanged() {
  FilterChangedEventManager.getInstance().triggerAll();
}

export function registerFilterChangedHandler(key: string, handler: () => void) {
  FilterChangedEventManager.getInstance().register(key, handler);
}

export function unregisterFilterChangedHandler(key: string) {
  FilterChangedEventManager.getInstance().unregister(key);
}

/**
 * Create an array of placeholder items for rendering FlashList skeletons.
 * Items include a predictable `key` that matches our `s-${index}` convention.
 *
 * @param count Number of skeleton items to create
 */
export function createSkeletonItems(count: number): any[] {
  return Array.from({ length: count }, (_, i) => ({ key: `s-${i}` }));
}

/**
 * Clear stored scroll position for a specific page
 * @param pageKey - Unique identifier for the page, the route path with leading slash (e.g., '/feeds/global', '/people/online')
 */
export function clearStoredScrollPosition(pageKey: string) {
  if (typeof window !== 'undefined' && window.sessionStorage) {
    try {
      sessionStorage.removeItem(`scroll_offset_${pageKey}`);
      sessionStorage.removeItem(`scroll_index_${pageKey}`);
    } catch (error) {
      // Silently handle any errors
    }
  }
}

// Disable browser's automatic scroll restoration to prevent interference with our custom restoration
if (
  Platform.OS === 'web' &&
  typeof window !== 'undefined' &&
  window.history &&
  'scrollRestoration' in window.history
) {
  window.history.scrollRestoration = 'manual';
}

// On full page reload (browser refresh), clear all stored scroll positions so the user starts at the top.
// Back/forward navigation and normal SPA navigation preserve positions as expected.
if (Platform.OS === 'web' && typeof window !== 'undefined') {
  try {
    const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    const isReload = navEntries.length > 0 && navEntries[0].type === 'reload';

    if (isReload) {
      const keysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key?.startsWith('scroll_offset_') || key?.startsWith('scroll_index_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => sessionStorage.removeItem(key));
    }
  } catch {
    // Silently handle errors
  }
}

interface UseInfiniteListUtilsOptions {
  queryKey: unknown[];
  flashListRef?: RefObject<React.ComponentRef<typeof FlashList<any>> | null>;
  onRefreshComplete?: () => void;
  pageKey?: string; // Unique key for both scroll position persistence and navigation-triggered refreshes
  disableRestoration?: boolean; // Disable scroll position restoration for this instance
}

interface RefreshOptions {
  scrollToEnd?: boolean;
  /** Shows the refresh button / pull indicator (web). Default true. */
  showRefreshingIndicator?: boolean;
  /** Shows the header-left spinner on large-screen native (e.g. tablet nav re-tap). Default false. */
  showHeaderRefetchSpinner?: boolean;
}

interface UseInfiniteListUtilsReturn {
  handleRefresh: (options?: RefreshOptions) => Promise<void>;
  scrollToTop: (animated?: boolean) => void;
  scrollToEnd: (animated?: boolean) => void;
  isUserRefreshing: boolean;
  /** True while the header-left refetch spinner should show (large-screen native only). */
  showHeaderRefetchIndicator: boolean;
  onScroll?: (event: any) => void; // For scroll offset-based persistence
  onViewableItemsChanged?: (info: any) => void; // For item index tracking (improves desktop accuracy)
  clearStoredPosition: () => void; // Expose method to clear stored position
}

/**
 * Utility hook for managing infinite lists with common patterns.
 * Provides standardized functionality for:
 * - Refreshing with data trimming (keeps only first page for mobile UX)
 * - Scroll to top functionality
 * - Query cancellation and invalidation
 * - Loading state management
 * - Custom post-refresh logic
 * - Scroll position persistence for web navigation (desktop and mobile):
 *   - Uses useFocusEffect to handle both Stack navigation (screen hidden/revealed)
 *     and full URL navigation (unmount/remount)
 *   - Saves scroll offset and first visible item index via sessionStorage
 *   - On remount (desktop web): uses scrollToIndex for accurate item targeting
 *   - On Stack reveal (mobile web): uses scrollToOffset for pixel-perfect restoration
 * - Navigation-triggered refresh system for active page refreshes
 *
 * The pageKey parameter serves three purposes:
 * 1. Enables scroll position persistence across web navigation
 * 2. Registers the page for navigation-triggered refreshes
 * 3. Registers the page for large-screen cross-tab navigation resets
 *
 * Pass the returned onScroll callback to FlashList to enable scroll persistence.
 * Optionally pass onViewableItemsChanged for better desktop web accuracy with
 * variable-height items (uses scrollToIndex instead of scrollToOffset on remount).
 *
 * On native, scroll position is naturally preserved by the navigation stack,
 * so persistence is only active on web (Platform.OS === 'web').
 */
export function useInfiniteListUtils({
  queryKey,
  flashListRef,
  onRefreshComplete,
  pageKey,
  disableRestoration,
}: UseInfiniteListUtilsOptions): UseInfiniteListUtilsReturn {
  const queryClient = useQueryClient();
  const isWeb = Platform.OS === 'web';
  const isLargeScreen = useIsLargeScreen();
  const [isUserRefreshing, setIsUserRefreshing] = useState(false);
  const [isHeaderRefetching, setIsHeaderRefetching] = useState(false);
  const showHeaderRefetchIndicator = useMemo(
    () => isLargeScreen && !isWeb && isHeaderRefetching,
    [isLargeScreen, isWeb, isHeaderRefetching]
  );
  const hasRestoredRef = useRef(false);
  const currentScrollOffset = useRef<number>(0);
  const currentFirstVisibleIndex = useRef<number>(-1);
  // Tracks whether this is a fresh mount (true) vs a Stack reveal (false).
  // On fresh mount, scrollToIndex is used for accuracy. On Stack reveal, scrollToOffset is pixel-perfect.
  const isFreshMountRef = useRef(true);

  const getStorageKey = useCallback(() => {
    if (!pageKey) return null;
    return `scroll_offset_${pageKey}`;
  }, [pageKey]);

  const saveScrollPosition = useCallback(() => {
    if (!isWeb || !pageKey) return;

    try {
      const storageKey = getStorageKey();
      if (storageKey && currentScrollOffset.current > 0) {
        sessionStorage.setItem(storageKey, currentScrollOffset.current.toString());
      }
      if (currentFirstVisibleIndex.current >= 0) {
        sessionStorage.setItem(
          `scroll_index_${pageKey}`,
          currentFirstVisibleIndex.current.toString()
        );
      }
    } catch (error) {
      // Silently handle any errors with scroll position saving
    }
  }, [pageKey, getStorageKey, isWeb]);

  const clearStoredPosition = useCallback(() => {
    if (!isWeb || !pageKey) return;

    try {
      const storageKey = getStorageKey();
      if (storageKey) {
        sessionStorage.removeItem(storageKey);
      }
      sessionStorage.removeItem(`scroll_index_${pageKey}`);
    } catch (error) {
      // Silently handle any errors
    }
  }, [pageKey, getStorageKey, isWeb]);

  // Track scroll offset for persistence
  const onScroll = useCallback(
    (event: any) => {
      if (!isWeb || !pageKey) return;

      const offset = event.nativeEvent.contentOffset.y;
      currentScrollOffset.current = offset;
    },
    [pageKey, isWeb]
  );

  // Track first visible item index for accurate restoration on remount
  const onViewableItemsChanged = useCallback(
    (info: any) => {
      if (!isWeb || !pageKey) return;

      if (info.viewableItems && info.viewableItems.length > 0) {
        currentFirstVisibleIndex.current = info.viewableItems[0].index;
      }
    },
    [pageKey, isWeb]
  );

  // Save scroll position when screen loses focus or unmounts (web only).
  // useFocusEffect fires cleanup on both Stack blur (screen hidden) and unmount (full navigation).
  useFocusEffect(
    useCallback(() => {
      if (!isWeb || !pageKey) return;

      return () => {
        saveScrollPosition();
      };
    }, [isWeb, pageKey, saveScrollPosition])
  );

  // Restore scroll position when screen gains focus or mounts (web only).
  // useFocusEffect fires on both Stack focus (screen revealed) and mount (full navigation).
  // Uses a retry mechanism to wait for FlashList readiness on initial mount.
  useFocusEffect(
    useCallback(() => {
      if (!isWeb || !pageKey || disableRestoration) return;

      let savedOffset: string | null = null;
      let savedIndex: string | null = null;
      try {
        savedOffset = sessionStorage.getItem(`scroll_offset_${pageKey}`);
        savedIndex = sessionStorage.getItem(`scroll_index_${pageKey}`);
      } catch {
        return;
      }
      if (!savedOffset && !savedIndex) return;

      const offset = savedOffset ? parseFloat(savedOffset) : 0;
      const index = savedIndex ? parseInt(savedIndex, 10) : -1;
      if (!(offset > 0) && index < 0) return;

      // On fresh mount (desktop Slot or browser back), FlashList has no measurements.
      // Use scrollToIndex for accurate item targeting (avoids accumulated estimation error).
      // On Stack reveal (mobile web), FlashList has measurements intact.
      // Use scrollToOffset for pixel-perfect restoration.
      const useIndexStrategy = isFreshMountRef.current && index >= 0;

      hasRestoredRef.current = false;
      let cancelled = false;
      let attempts = 0;
      const maxAttempts = 30; // ~2.4s at 80ms intervals
      let intervalId: ReturnType<typeof setInterval> | null = null;

      const tryRestore = (): boolean => {
        if (cancelled || hasRestoredRef.current || !flashListRef?.current) return false;

        try {
          if (useIndexStrategy) {
            // @ts-ignore - FlashList scrollToIndex method
            flashListRef.current.scrollToIndex({ index, animated: false });
          } else if (offset > 0) {
            // @ts-ignore - FlashList scrollToOffset method
            flashListRef.current.scrollToOffset({ offset, animated: false });
          } else {
            return false;
          }

          hasRestoredRef.current = true;
          isFreshMountRef.current = false;
          return true;
        } catch {
          return false;
        }
      };

      // First attempt after a frame to let FlashList initialize
      requestAnimationFrame(() => {
        if (cancelled) return;
        if (tryRestore()) return;

        // Poll until FlashList is ready
        intervalId = setInterval(() => {
          attempts++;
          if (cancelled || attempts >= maxAttempts || tryRestore()) {
            if (intervalId) clearInterval(intervalId);
          }
        }, 80);
      });

      return () => {
        cancelled = true;
        if (intervalId) clearInterval(intervalId);
      };
    }, [isWeb, pageKey, disableRestoration, flashListRef])
  );

  const scrollToTop = useCallback(
    (animated = false) => {
      if (flashListRef?.current) {
        // @ts-ignore - FlashList scrollToOffset method
        flashListRef.current.scrollToOffset({ offset: 0, animated });
      }
    },
    [flashListRef]
  );

  const handleNavigationReset = useCallback(() => {
    hasRestoredRef.current = false;
    currentScrollOffset.current = 0;
    currentFirstVisibleIndex.current = -1;
    clearStoredPosition();

    // Defer the scroll so it runs AFTER the triggering navigation has applied
    // and the destination tab is focused/measured. Calling scrollToOffset on a
    // hidden tab's FlashList can be undone once it becomes visible (e.g. by
    // maintainVisibleContentPosition re-anchoring during re-measure), which is
    // why feeds in particular needs this guard.
    requestAnimationFrame(() => {
      scrollToTop();
    });
  }, [clearStoredPosition, scrollToTop]);

  const scrollToEnd = useCallback(
    (animated = false) => {
      if (flashListRef?.current) {
        // @ts-ignore - FlashList scrollToEnd method
        flashListRef.current.scrollToEnd({ animated });
      }
    },
    [flashListRef]
  );

  const handleRefresh = useCallback(
    async (options?: RefreshOptions) => {
      const showRefreshingIndicator = options?.showRefreshingIndicator !== false;
      const showHeaderRefetchSpinner = options?.showHeaderRefetchSpinner === true;
      if (showRefreshingIndicator) {
        setIsUserRefreshing(true);
      }
      if (showHeaderRefetchSpinner) {
        setIsHeaderRefetching(true);
      }

      try {
        // Clear any stored scroll position since we're refreshing
        clearStoredPosition();
        hasRestoredRef.current = false;
        currentScrollOffset.current = 0;
        currentFirstVisibleIndex.current = -1;

        // Cancel any outgoing queries
        queryClient.cancelQueries({ queryKey });

        // Keep only the first page (important for mobile UX)
        queryClient.setQueryData(queryKey, (oldData: any) => {
          if (oldData) {
            return {
              ...oldData,
              pages: oldData.pages.slice(0, 1),
              pageParams: oldData.pageParams.slice(0, 2),
            };
          }
          return oldData;
        });

        // Invalidate and refetch
        await queryClient.invalidateQueries({ queryKey });

        // Extract scrollToEnd option, defending against event objects
        const shouldScrollEnd =
          options && typeof options === 'object' && options.scrollToEnd === true;

        // Fixes FlashList issue with new items not being visible after refresh
        setTimeout(() => {
          if (shouldScrollEnd) {
            scrollToEnd();
          } else {
            scrollToTop();
          }
        }, 0);

        // Execute custom post-refresh logic
        onRefreshComplete?.();
      } finally {
        if (showRefreshingIndicator) {
          setIsUserRefreshing(false);
        }
        if (showHeaderRefetchSpinner) {
          setIsHeaderRefetching(false);
        }
      }
    },
    [queryKey, scrollToTop, scrollToEnd, onRefreshComplete, queryClient, clearStoredPosition]
  );

  const handleNavigationRefresh = useCallback(() => {
    if (isWeb) {
      return handleRefresh();
    }
    return handleRefresh({
      showRefreshingIndicator: false,
      showHeaderRefetchSpinner: true,
    });
  }, [isWeb, handleRefresh]);

  // Register/unregister for navigation-triggered refreshes
  useEffect(() => {
    if (!pageKey) return;

    const eventManager = PageRefreshEventManager.getInstance();
    eventManager.registerRefreshHandler(pageKey, handleNavigationRefresh);

    return () => {
      eventManager.unregisterRefreshHandler(pageKey);
    };
  }, [pageKey, handleNavigationRefresh]);

  // Register/unregister for navigation resets used by large-screen cross-tab navigation.
  useEffect(() => {
    if (!pageKey) return;

    const eventManager = PageNavigationResetEventManager.getInstance();
    eventManager.registerResetHandler(pageKey, handleNavigationReset);

    return () => {
      eventManager.unregisterResetHandler(pageKey);
    };
  }, [pageKey, handleNavigationReset]);

  // Track focus state so the filter-changed handler knows whether to act immediately
  const isFocusedRef = useRef(true);
  useFocusEffect(
    useCallback(() => {
      isFocusedRef.current = true;
      return () => {
        isFocusedRef.current = false;
      };
    }, [])
  );

  // On focus, detect if a filter change happened while this screen was hidden and reset
  const lastFilterVersionRef = useRef(getFilterVersion());

  // Register for filter-changed events. Focused screens reset immediately;
  // non-focused screens skip (they'll be handled by the focus effect below).
  useEffect(() => {
    if (!pageKey) return;

    const manager = FilterChangedEventManager.getInstance();
    manager.register(pageKey, () => {
      if (!isFocusedRef.current) return;
      lastFilterVersionRef.current = getFilterVersion();
      queueMicrotask(() => {
        queryClient.resetQueries({ queryKey, exact: true });
        scrollToTop();
        clearStoredPosition();
      });
    });

    return () => {
      manager.unregister(pageKey);
    };
  }, [pageKey, queryKey, queryClient, scrollToTop, clearStoredPosition]);
  useFocusEffect(
    useCallback(() => {
      const current = getFilterVersion();
      if (lastFilterVersionRef.current !== current) {
        lastFilterVersionRef.current = current;
        queryClient.resetQueries({ queryKey, exact: true });
        scrollToTop();
        clearStoredPosition();
      }
    }, [queryKey, queryClient, scrollToTop, clearStoredPosition])
  );

  return {
    handleRefresh,
    scrollToTop,
    scrollToEnd,
    isUserRefreshing,
    showHeaderRefetchIndicator,
    onScroll: pageKey ? onScroll : undefined,
    onViewableItemsChanged: pageKey ? onViewableItemsChanged : undefined,
    clearStoredPosition,
  };
}
